import ApiService from './api.service'
import { TokenService } from './storage.service'


class AuthenticationError extends Error {
    constructor(errorCode, message) {
        super(message)
        this.name = this.constructor.name
        this.message = message
        this.errorCode = errorCode
    }
}

const UserService = {
    /**
     * Login the user and store the access token to TokenService. 
     * 
     * @returns access_token
     * @throws AuthenticationError 
    **/
    login: async function(email, password) {
        let loginParas = 'UserName=' + encodeURIComponent(email)
        loginParas += '&Password=' + encodeURIComponent(password)
        loginParas += '&grant_type=password'
        // Forward trusted-device token if present so the server can skip MFA.
        const deviceToken = TokenService.getMfaDeviceToken()
        if (deviceToken) {
            loginParas += '&mfa_device_token=' + encodeURIComponent(deviceToken)
        }
        const requestData = {
            method: 'post',
            url: "/Token",
            data: loginParas
        }

        try {
            const response = await ApiService.customRequest(requestData)

            // save basic user info here
            const userData = {
                id: parseInt(response.data.id),
                userName: response.data.userName,
                fullName: response.data.fullName,
                roles: response.data.roles,
                expDate: response.data['.expires'],
            }
            TokenService.saveUserData(userData)

            TokenService.saveToken(response.data.access_token)
            TokenService.saveRefreshToken(response.data.refresh_token)
            ApiService.setHeader()

            ApiService.mount401Interceptor();

            return { mfaRequired: false, accessToken: response.data.access_token }
        } catch (error) {
            // The MFA challenge comes back as HTTP 400 with body
            //   { error: "mfa_required", error_description: "<JSON {mfa_token, masked_email, ...}>" }
            // because OWIN's OAuth /Token endpoint can't return a custom 200 from
            // GrantResourceOwnerCredentials. Detect and surface to the auth module.
            if (error.response
                && error.response.data
                && error.response.data.error === 'mfa_required') {
                let challenge = {}
                try { challenge = JSON.parse(error.response.data.error_description) } catch (e) { challenge = {} }
                return {
                    mfaRequired: true,
                    mfaMethod: challenge.mfa_method,
                    mfaToken: challenge.mfa_token,
                    maskedEmail: challenge.masked_email,
                    expiresIn: challenge.expires_in
                }
            }
            throw new AuthenticationError(error.response.status, error.response.data.error_description)
        }
    },

    /**
     * Refresh the access token.
    **/
    refreshToken: async function() {
        const refreshToken = TokenService.getRefreshToken()

        const requestData = {
            method: 'post',
            url: "/o/token/",
            data: {
                grant_type: 'refresh_token',
                refresh_token: refreshToken
            },
            auth: {
                username: process.env.VUE_APP_CLIENT_ID,
                password: process.env.VUE_APP_CLIENT_SECRET
            }
        }

        try {
            const response = await ApiService.customRequest(requestData)

            TokenService.saveToken(response.data.access_token)
            TokenService.saveRefreshToken(response.data.refresh_token)
            // Update the header in ApiService
            ApiService.setHeader()

            return response.data.access_token
        } catch (error) {
            throw new AuthenticationError(error.response.status, error.response.data.detail)
        }

    },

    /**
     * Logout the current user by removing the token from storage. 
     * 
     * Will also remove `Authorization Bearer <token>` header from future requests.
    **/
    logout() {
        // Remove the token and remove Authorization header from Api Service as well 
        TokenService.removeUserData()
        TokenService.removeToken()
        TokenService.removeRefreshToken()
        ApiService.removeHeader()
        
        ApiService.unmount401Interceptor()
    }
}

export default UserService

export { UserService, AuthenticationError }